import { motion } from "framer-motion";
import { Leaf, Truck, ArrowRight, Sparkles } from "lucide-react";
import { DemoBar } from "@/components/DemoBar";
import { CASES } from "@/data/cases";

const juiceImg = CASES.find((c) => c.slug === "kede-juice").image;
const barImg = CASES.find((c) => c.slug === "kede-mobile-juice").image;

const JUICES = [
  { name: "Grøn Morgen", desc: "Grønkål, æble, ingefær og lime", price: "45 kr" },
  { name: "Gylden Ingefær", desc: "Appelsin, gulerod og frisk ingefær", price: "49 kr" },
  { name: "Bær Boost", desc: "Blåbær, hindbær og banan", price: "45 kr" },
];

export default function KedeJuiceDemo() {
  return (
    <div data-testid="demo-kede-juice" className="min-h-screen bg-[#F3F8F1] text-[#14240F]">
      <DemoBar name="KEDE-Juice" />

      <header className="flex items-center justify-between border-b border-[#14240F]/10 bg-white/60 px-6 py-5 backdrop-blur-md md:px-12">
        <p className="font-syne text-xl font-extrabold tracking-tight">
          KEDE<span className="text-[#3E9B4F]">-Juice</span>
        </p>
        <p className="hidden text-xs uppercase tracking-[0.25em] text-[#14240F]/50 md:block">
          En del af KEDE Health Platform
        </p>
        <button data-testid="kede-juice-order-button" className="rounded-full bg-[#3E9B4F] px-6 py-2.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-105">
          Bestil juice
        </button>
      </header>

      <section className="grid grid-cols-1 items-center gap-12 px-6 py-16 md:grid-cols-2 md:px-12 md:py-24">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-[#3E9B4F]">
            <Leaf className="h-4 w-4" /> Friskpresset hver morgen
          </p>
          <h1 className="mt-4 font-syne text-4xl font-extrabold uppercase leading-[0.92] tracking-tighter md:text-6xl">
            Juice med <span className="text-[#3E9B4F]">dokumenteret</span> indhold
          </h1>
          <p className="mt-6 max-w-md text-[#14240F]/70">
            Hver flaske fortæller præcist, hvad den indeholder — vitaminer, mineraler og ikke en dråbe
            tilsat sukker. Sundhed du kan smage.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="kede-juice-hero-cta" className="group flex items-center gap-2 rounded-full bg-[#3E9B4F] px-8 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-105">
              Se menuen <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <span className="flex items-center gap-2 rounded-full border border-[#14240F]/15 bg-white px-6 py-4 text-sm text-[#14240F]/70">
              <Sparkles className="h-4 w-4 text-[#3E9B4F]" /> 100% rå ingredienser
            </span>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="overflow-hidden rounded-[2rem] shadow-2xl shadow-[#3E9B4F]/20"
        >
          <img src={juiceImg} alt="Friskpresset grøn juice fra KEDE-Juice" data-testid="kede-juice-hero-image" className="aspect-[4/3] w-full object-cover" />
        </motion.div>
      </section>

      <section className="bg-white px-6 py-20 md:px-12">
        <h2 className="font-syne text-3xl font-extrabold uppercase tracking-tighter md:text-5xl">Dagens menu</h2>
        <div data-testid="kede-juice-menu" className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {JUICES.map((j, i) => (
            <motion.div
              key={j.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="rounded-2xl border border-[#14240F]/10 bg-[#F3F8F1] p-8 transition-transform duration-300 hover:-translate-y-1"
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-syne text-xl font-bold">{j.name}</h3>
                <p className="font-syne text-xl font-bold text-[#3E9B4F]">{j.price}</p>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-[#14240F]/70">{j.desc}</p>
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#3E9B4F]">0% tilsat sukker</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 items-center gap-12 px-6 py-20 md:grid-cols-2 md:px-12">
        <div className="overflow-hidden rounded-[2rem]">
          <img src={barImg} alt="KEDE's mobile juicebar" className="aspect-[4/3] w-full object-cover" loading="lazy" />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-[#3E9B4F]">
            <Truck className="h-4 w-4" /> KEDE-mobile-Juice
          </p>
          <h2 className="mt-4 font-syne text-3xl font-extrabold uppercase tracking-tighter md:text-5xl">
            Juicebaren kommer til dig
          </h2>
          <p className="mt-6 max-w-md text-[#14240F]/70">
            Book vores mobile juicebar til dit event, marked eller firmadag — vi presser på stedet.
          </p>
          <button data-testid="kede-juice-book-cta" className="mt-8 rounded-full bg-[#14240F] px-8 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-105">
            Book juicebaren
          </button>
        </motion.div>
      </section>

      <footer className="border-t border-[#14240F]/10 px-6 py-10 text-center text-sm text-[#14240F]/60 md:px-12">
        KEDE-Juice · En del af KEDE Health Platform · hej@kede-juice.dk
      </footer>
    </div>
  );
}
